import { theme, useResponsive } from "@product/scmp-sdk";
import { useEffect, useMemo } from "react";
import type { FunctionComponent } from "react";

import { AdSlot } from "~/components/advertisement/ad-slots/ad-slot";
import { useAdSlotWithRefresh } from "~/components/advertisement/ad-slots/ad-slot/hooks";
import type { UniqueArticlesPerSectionItem } from "~/components/magazines-style/helpers";
import { useStoryStyleContext } from "~/components/story-style/contexts";
import { ArrowLeft, ArrowRight } from "~/components/story-style/story-style-arrow";
import { StoryStyleCard } from "~/components/story-style/story-style-card";
import { StoryStyleCloseButton } from "~/components/story-style/story-style-close-button";
import { StoryStyleOverlayTutorial } from "~/components/story-style/story-style-layout/story-style-overlay-tutorial";
import { StoryStyleNewsletterSignUpCard } from "~/components/story-style/story-style-newsletter-sign-up-card";
import { StoryStyleProgressBar } from "~/components/story-style/story-style-progress-bar";

import { AdCardIndexList } from "./consts";
import { getCurrentDisplayType } from "./helpers";
import {
  BottomBannerAdSlotWrapper,
  Container,
  Section,
  SliderWrapper,
  Slides,
  StyledStoryStyleAdCard,
} from "./styles";

type Props = {
  className?: string;
  layoutBreakpoint: string;
  uniqueArticlesPerSection: UniqueArticlesPerSectionItem[];
};

export const StoryStyleLayout: FunctionComponent<Props> = ({
  className,
  layoutBreakpoint,
  uniqueArticlesPerSection,
}) => {
  const {
    articleCardState,
    currentPageIndex,
    handleGoToNextPage,
    handleGoToPreviousPage,
    maximumCardIndex,
    totalArticleCount,
  } = useStoryStyleContext();

  const currentDisplayType = getCurrentDisplayType({
    currentIndex: currentPageIndex,
    maximumCardIndex,
  });

  const articleCardIndex = useMemo(
    () => articleCardState?.articleCardIndex ?? 0,
    [articleCardState?.articleCardIndex],
  );

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const { props: adSlotProps, refresh: refreshAdSlot } = useAdSlotWithRefresh({
    adUnit: "m_banner3",
    breakpoint: layoutBreakpoint,
    sizes: [
      [320, 50],
      [300, 50],
      [300, 100],
      [320, 100],
    ],
    zone: "interstitial_style/card",
  });

  useEffect(() => {
    if (AdCardIndexList.includes(articleCardIndex)) {
      return;
    }
    refreshAdSlot();
  }, [articleCardIndex, refreshAdSlot]);

  const handleRenderCard = () => {
    let currentIndex = 0;
    return uniqueArticlesPerSection.map(({ articleEdges, section }) =>
      articleEdges.map(article => (
        <StoryStyleCard
          hasBottomBannerAd={currentIndex !== 0}
          hasCoverLogo={currentIndex === 0}
          index={currentIndex}
          isHidden={currentDisplayType !== "article" || currentIndex++ !== articleCardIndex}
          key={article.node.entityId}
          reference={article.node}
          section={section.name}
        />
      )),
    );
  };

  return (
    <Container className={className}>
      <SliderWrapper>
        <ArrowLeft disabled={currentPageIndex === 0} noStyle onClick={handleGoToPreviousPage} />
        <ArrowRight
          disabled={currentPageIndex === maximumCardIndex}
          noStyle
          onClick={handleGoToNextPage}
        />
        <Slides>
          {isMobile && <StoryStyleOverlayTutorial />}
          <StyledStoryStyleAdCard
            $isHidden={currentDisplayType !== "advertisement"}
            breakpoint={layoutBreakpoint}
          />
          <Section>
            {currentDisplayType === "newsletter-sign-up" && (
              <>
                <StoryStyleNewsletterSignUpCard
                  handleGoToNextPage={handleGoToNextPage}
                  handleGoToPreviousPage={handleGoToPreviousPage}
                />
                <StoryStyleCloseButton />
              </>
            )}
            {currentDisplayType === "article" && (
              <>
                <StoryStyleProgressBar
                  articleCardIndex={articleCardIndex}
                  totalCardLength={totalArticleCount}
                />
                <StoryStyleCloseButton />
              </>
            )}
            {handleRenderCard()}
            <BottomBannerAdSlotWrapper
              $isHidden={currentDisplayType !== "article" || articleCardIndex === 0}
            >
              <AdSlot {...adSlotProps} />
            </BottomBannerAdSlotWrapper>
          </Section>
        </Slides>
      </SliderWrapper>
    </Container>
  );
};

StoryStyleLayout.displayName = "StoryStyleLayout";
